import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Search, MessageSquare, ArrowLeft } from "lucide-react";

export default function Messages() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  const loadConversations = useCallback(async () => {
    const { data } = await api.get("/messages/conversations");
    setConversations(data);
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const loadThread = useCallback(async (other) => {
    const { data } = await api.get(`/messages/${other}`);
    setThread(data);
  }, []);

  useEffect(() => { if (userId) loadThread(userId); else setThread(null); }, [userId, loadThread]);

  useEffect(() => {
    if (query.length < 2) return setResults([]);
    const t = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(query)}`).then(({ data }) => setResults(data));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const send = async () => {
    if (!body.trim() || !userId) return;
    await api.post("/messages", { recipient_id: userId, body });
    setBody("");
    loadThread(userId);
    loadConversations();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
      <p className="font-tech text-xs uppercase tracking-widest brand-text">Direct messages</p>
      <h1 className="font-display text-4xl font-bold mb-6">Messages</h1>

      <div className="lg:grid lg:grid-cols-[320px_1fr] gap-6 lg:h-[70vh]">
        <aside className={`${userId ? "hidden lg:block" : "block"} surface border rounded-2xl p-4 overflow-y-auto mb-6 lg:mb-0 max-h-[70vh] lg:max-h-none`}>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-page" />
            <input data-testid="user-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a collector…" className="w-full surface border rounded-full pl-9 pr-3 py-2 text-sm" />
          </div>
          {results.length > 0 && (
            <div className="mb-4 space-y-1">
              {results.map((u) => (
                <button key={u.id} data-testid={`search-user-${u.id}`} onClick={() => { navigate(`/messages/${u.id}`); setQuery(""); setResults([]); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-black/5 text-sm flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full grid place-items-center text-xs font-bold" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>{(u.username || "?")[0].toUpperCase()}</span>
                  {u.username}
                </button>
              ))}
            </div>
          )}
          <p className="font-tech text-xs uppercase tracking-widest text-muted-page mb-2">Conversations</p>
          {conversations.length === 0 ? <p className="text-sm text-muted-page">No conversations yet.</p> : conversations.map((c) => (
            <button key={c.other_id} data-testid={`conversation-${c.other_id}`} onClick={() => navigate(`/messages/${c.other_id}`)} className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 flex items-center gap-2 ${userId === c.other_id ? "bg-black/5" : "hover:bg-black/[0.03]"}`}>
              <span className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold shrink-0" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>{(c.other_name || "?")[0].toUpperCase()}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between">
                  <span className="font-semibold text-sm truncate">{c.other_name}</span>
                  {c.unread > 0 && <span className="text-[10px] px-1.5 rounded-full text-white" style={{ background: "var(--brand)" }}>{c.unread}</span>}
                </span>
                <span className="block text-xs text-muted-page truncate">{c.last_message}</span>
              </span>
            </button>
          ))}
        </aside>

        <section className={`${userId ? "flex" : "hidden lg:flex"} flex-col surface border rounded-2xl overflow-hidden h-[75vh] lg:h-auto`}>
          {thread ? (
            <>
              <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--page-border)" }}>
                <button onClick={() => navigate("/messages")} data-testid="messages-back-btn" className="lg:hidden p-1.5 -ml-1.5 rounded-lg hover:bg-black/5"><ArrowLeft className="w-5 h-5" /></button>
                <span className="w-8 h-8 rounded-full grid place-items-center text-xs font-bold" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>{(thread.other.username || "?")[0].toUpperCase()}</span>
                <span className="font-display text-xl font-bold">{thread.other.username}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-2" data-testid="message-thread">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${m.sender_id === user.id ? "ml-auto text-white" : "surface border"}`} style={m.sender_id === user.id ? { background: "var(--brand)" } : {}}>
                    {m.body}
                  </div>
                ))}
                {thread.messages.length === 0 && <p className="text-center text-muted-page text-sm mt-10">Say hello 👋</p>}
              </div>
              <div className="p-4 border-t flex gap-2" style={{ borderColor: "var(--page-border)" }}>
                <input data-testid="message-input" value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 surface border rounded-full px-4 py-2.5" />
                <button data-testid="send-message-btn" onClick={send} className="w-11 h-11 rounded-full grid place-items-center text-white shrink-0" style={{ background: "var(--brand)" }}><Send className="w-5 h-5" /></button>
              </div>
            </>
          ) : (
            <div className="flex-1 grid place-items-center text-muted-page">
              <div className="text-center"><MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>Select a conversation or search for a collector.</p></div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
